import { useCallback, useEffect, useRef } from 'react';
import { useEvent, useTheme } from '@/modules/common';
import {
  bridgeAtom,
  ChatWindowHeights,
  chatWindowHeightsAtom,
  isOpenChatAtom,
  isSemiOpenChatAtom,
  semiOpenSeenPagesAtom,
  useAtom,
  useValue,
} from '@/store';
import { OPEN_CHAT_STORAGE } from '@/store/constants';
import { AppEvents, updateAppParams } from '@/utils';
import { useIsWidgetDisabled } from './useIsWidgetDisabled';

const SEMI_OPEN_MAX_HEIGHT = 450;

const getFullHeight = (theme: ReturnType<typeof useTheme>, isMobile: boolean) => {
  const mobileHeight =
    (theme.container?.mobile as Record<string, unknown>)?.height ??
    (theme.container?.mobile as Record<string, unknown>)?.maxHeight;
  const desktopHeight =
    (theme.container?.desktop as Record<string, unknown>)?.height ??
    (theme.container?.desktop as Record<string, unknown>)?.maxHeight;
  if (isMobile) {
    return typeof mobileHeight === 'string' && mobileHeight !== '' ? mobileHeight : '70dvh';
  }
  return typeof desktopHeight === 'string' && desktopHeight !== '' ? desktopHeight : `${window.innerHeight - 100}px`;
};

const applyFullOpen = (fullHeight: string) => {
  updateAppParams({
    theme: {
      container: {
        desktop: {
          maxHeight: fullHeight,
        },
        mobile: {
          maxHeight: 'none',
          height: fullHeight,
        },
      },
      prompts: {
        styles: {
          display: 'flex',
        },
      },
    },
  });
};

const getSemiOpenHeight = (heights: ChatWindowHeights) => {
  const calculatedHeight = Math.ceil(Object.values(heights).reduce((acc, value) => acc + value, 0));
  return Math.min(calculatedHeight, SEMI_OPEN_MAX_HEIGHT);
};

const applySemiOpen = (semiOpenHeight: string) => {
  updateAppParams({
    theme: {
      container: {
        desktop: {
          maxHeight: semiOpenHeight,
        },
        mobile: {
          maxHeight: 'none',
          height: semiOpenHeight,
        },
      },
      prompts: {
        styles: {
          display: 'none',
        },
      },
    },
  });
};

const NO_OP = () => {};

export const useSemiOpenChat = () => {
  const { semiOpenConfig, pageType, onProactiveOpen } = useValue(bridgeAtom);
  // ref keeps the timer effect from restarting when the bridge object changes
  const onProactiveOpenRef = useRef(onProactiveOpen);
  const delay = semiOpenConfig?.delay || 5_000;
  const [semiOpenSeenPages, setSemiOpenSeenPages] = useAtom(semiOpenSeenPagesAtom);
  const [isOpenChat, setIsOpenChat] = useAtom(isOpenChatAtom);
  const chatWindowHeights = useValue(chatWindowHeightsAtom);
  const [isSemiOpen, setIsSemiOpen] = useAtom(isSemiOpenChatAtom);
  const theme = useTheme();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMobile = window.matchMedia(`(${theme.mediaMobile})`).matches;
  const isWidgetDisabled = useIsWidgetDisabled();

  const isPagesMode = Boolean(semiOpenConfig?.pageTypes?.length);
  const shouldSkipSemiOpen =
    !semiOpenConfig || // semi-open is not enabled
    (isOpenChat && !isSemiOpen) || // chat open in full mode
    (isPagesMode && !semiOpenConfig?.pageTypes?.includes(pageType)) || // page type is not in the list of page types to semi-open
    (isPagesMode && semiOpenSeenPages.includes(pageType)) || // already seen in page mode
    (!isPagesMode && semiOpenSeenPages.length >= 1); // already seen in session mode

  const fullHeight = useRef('');
  const semiOpenHeight = getSemiOpenHeight(chatWindowHeights);

  useEffect(() => {
    onProactiveOpenRef.current = onProactiveOpen;
  }, [onProactiveOpen]);

  // effect that decides whether to semi-open or not
  useEffect(() => {
    if (isWidgetDisabled) {
      return;
    }
    if (shouldSkipSemiOpen) {
      setSemiOpenSeenPages((prev) => Array.from(new Set([...prev, pageType])));
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    }
    timerRef.current = setTimeout(() => {
      setIsOpenChat(true);
      setIsSemiOpen(true);
      // we don't want to fully open chat on page change/refresh
      sessionStorage.setItem(OPEN_CHAT_STORAGE, 'false');
      setSemiOpenSeenPages((prev) => Array.from(new Set([...prev, pageType])));
      onProactiveOpenRef.current?.();
    }, delay);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isWidgetDisabled, shouldSkipSemiOpen, delay, pageType, setIsOpenChat, setIsSemiOpen, setSemiOpenSeenPages]);

  // apply correct height (full or semi) to the chat window (2 effects)
  useEffect(() => {
    if (!fullHeight.current) {
      fullHeight.current = getFullHeight(theme, isMobile);
    }
  }, [theme, isMobile]);
  useEffect(() => {
    if (isSemiOpen) {
      applySemiOpen(`${semiOpenHeight}px`);
    } else {
      applyFullOpen(fullHeight.current);
    }
  }, [semiOpenHeight, isSemiOpen]);
  //

  const onExternalTrigger = useCallback(() => {
    if (isOpenChat && !isSemiOpen) {
      return;
    }
    if (isSemiOpen) {
      return;
    }
    setIsOpenChat(true);
    setIsSemiOpen(true);
    sessionStorage.setItem(OPEN_CHAT_STORAGE, 'false');
    onProactiveOpenRef.current?.();
  }, [isOpenChat, isSemiOpen, setIsOpenChat, setIsSemiOpen]);

  useEvent({ name: AppEvents.TRIGGER_SEMI_OPEN, handler: onExternalTrigger });

  const onChatWindowClick = useCallback(() => {
    setIsSemiOpen(false);
    applyFullOpen(fullHeight.current);
  }, [setIsSemiOpen]);

  return {
    onChatWindowClick: Boolean(semiOpenConfig) || isSemiOpen ? onChatWindowClick : NO_OP,
  };
};
